import { TextField } from 'components/Field/TextField';
import { ICONS } from 'components/Icon';
import { IconButton } from 'components/IconButton';
import { ModalScreens } from 'components/Navigation/types';
import { ViewWithSeparators } from 'components/ViewWithSeparators';
import { useAlert } from 'hooks/useAlert';
import { useSelector } from 'hooks/useSelector';
import { animateNext } from 'lib/animateNext';
import { theme } from 'lib/theme';
import React from 'react';
import { View } from 'react-native';
import { useDispatch } from 'react-redux';
import { NAVIGATE_MODAL, OPEN_BARCODE_SCANNER } from 'store/actions';
import {
  doAseDownloadElementPdfsZip,
  doAseFieldInstanceSearchBarcodeForGroup,
  doAseFieldInstanceSearchPin,
  doAseLoadPlans,
  doAseSetGroupFilter,
} from 'store/assessments/actions';
import {
  selectAseTplFieldGroup,
  selectAseFieldGroupStats,
  selectAseFieldInstancePinsForGroup,
  selectAseIsEditable,
  selectAseFieldInstanceQrsForGroups,
  selectAseTplFieldToGroup,
  selectAseIsOnline,
  selectAseFieldGroupFilter,
  selectAseExplorerOptions,
  selectAseIsApproved,
} from 'store/assessments/selectors';
import { InstanceParentParams } from 'store/assessments/types';
import { Menu } from 'components/Menu';
import { CountChips } from '../CountChips';
import { NAVIGATION_HEADER_HEIGHT } from 'components/NavigationHeader';

type Props = { groupId: string; parent: InstanceParentParams };

export const RepeatableListFilter: React.FC<Props> = ({ groupId, parent }) => {
  const dispatch = useDispatch();
  const alert = useAlert();
  const renderCountRef = React.useRef(0);
  renderCountRef.current += 1;

  const filterKey = 'added';
  const groupFilterKey = `${filterKey}-${groupId}`;
  const groupFilter = useSelector((state) =>
    selectAseFieldGroupFilter(state, groupFilterKey),
  );
  const explorerOptions = useSelector(selectAseExplorerOptions);
  const searchValue = groupFilter.search;
  const autoFocusSearchFieldRef = React.useRef(false);
  const [showSearch, setShowSearch] = React.useState<boolean>(!!searchValue);

  const isEditable = useSelector(selectAseIsEditable);
  const isOnline = useSelector(selectAseIsOnline);
  const isApproved = useSelector(selectAseIsApproved);

  const group = useSelector((state) => selectAseTplFieldGroup(state, groupId));
  const parentGroup = useSelector((state) =>
    selectAseTplFieldToGroup(state, parent?.fieldId || '-1'),
  );

  const groupStats = useSelector((state) =>
    selectAseFieldGroupStats(state, {
      groupId,
      parent,
      inAssessmentOnly: false,
    }),
  );

  const groupPins = useSelector((state) =>
    selectAseFieldInstancePinsForGroup(state, {
      groupId,
      parent: !isEditable || isOnline ? parent : null,
      inAssessmentOnly: !isEditable || isOnline,
    }),
  ).map((pin) => {
    if (parentGroup && groupStats.assessmentInstanceIds.includes(pin.id)) {
      return {
        ...pin,
        group: `In ${parentGroup.calculatedInfo.singularName}`,
        color: theme.colors.purple,
      };
    }
    return pin;
  });

  const groupQRs = useSelector((state) =>
    selectAseFieldInstanceQrsForGroups(state, {
      groupId,
      parent: !isEditable || isOnline ? parent : null,
      inAssessmentOnly: !isEditable || isOnline,
    }),
  );

  const handleSearch = React.useCallback(
    (search: string) => {
      if (search) {
        setShowSearch(true);
      }
      dispatch(
        doAseSetGroupFilter({
          groupId: groupFilterKey,
          data: { ...groupFilter, search },
        }),
      );
    },
    [dispatch, groupFilter, groupFilterKey],
  );

  const blurIsFocusedRef = React.useRef(false);
  const handleSearchFocus = React.useCallback(() => {
    blurIsFocusedRef.current = true;
  }, []);

  const handleSearchBlur = React.useCallback(() => {
    blurIsFocusedRef.current = false;
    if (!searchValue) {
      animateNext(() => setShowSearch(false));
    }
  }, [searchValue]);

  React.useEffect(() => {
    if (!blurIsFocusedRef.current) {
      setShowSearch(!!searchValue);
    }
  }, [searchValue]);

  if (!group) {
    return null;
  }

  const handleFindByQrPress = () =>
    dispatch(
      OPEN_BARCODE_SCANNER({
        text:
          isEditable && !isOnline
            ? `scan to find an existing ${group.calculatedInfo.singularName.toLowerCase()} or to create a new one`
            : `scan to find an existing ${group.calculatedInfo.singularName.toLowerCase()}`,
        actionAfter: doAseFieldInstanceSearchBarcodeForGroup({
          groupId,
          data: '',
          parent,
        }),
      }),
    );

  const handleFindByPlanPress = () =>
    !groupPins.length
      ? alert({
          text: `No ${group.name.toLowerCase()} have pins yet. You'll be able to find them by their pins once there are`,
        })
      : dispatch(
          doAseLoadPlans({
            findNoun: group.calculatedInfo.singularName,
            pins: groupPins,
            actionAfterFind: doAseFieldInstanceSearchPin({
              groupId,
              id: '',
              parent,
            }),
          }),
        );

  const handleDownloadElementZip = () =>
    isApproved
      ? dispatch(
          doAseDownloadElementPdfsZip({
            groupId,
            parent,
          }),
        )
      : alert({
          text: 'A ZIP of all element PDFs can only be downloaded once this assessment has been approved',
        });

  const { qrFieldId, pinFieldId } = group.calculatedInfo;

  const showQRButton =
    !!qrFieldId && ((isEditable && !isOnline) || !!groupQRs.length);
  const showPinButton =
    !!pinFieldId && ((isEditable && !isOnline) || !!groupPins.length);

  const showCountChips =
    !!groupStats.questionsCount ||
    !!groupStats.checklistsCount ||
    !!groupStats.actionsCount ||
    !!groupStats.controlsCount;

  const shouldAutoFocusSearchField = autoFocusSearchFieldRef.current;
  autoFocusSearchFieldRef.current = false;

  const hasEnabledFilterOptions =
    !explorerOptions.findingsNewReviewed ||
    !explorerOptions.questionsNegative ||
    !explorerOptions.questionsPositive ||
    (isEditable &&
      (!explorerOptions.questionsUnanswered ||
        !explorerOptions.findingsUnreviewed)) ||
    !!Object.keys(groupFilter.checks).length ||
    !!groupFilter.availabilityCategories?.length ||
    groupFilter.requiredFields;

  return (
    <ViewWithSeparators>
      <View
        style={{
          flexDirection: 'row',
          paddingHorizontal: theme.paddingHorizontal,
          minHeight: NAVIGATION_HEADER_HEIGHT,
          alignItems: 'center',
        }}
      >
        {showCountChips ? (
          <CountChips
            questions={groupStats.questionsCount}
            questionsCompleted={groupStats.completedQuestionsCount}
            checklists={groupStats.checklistsCount}
            checklistsCompleted={groupStats.completedChecklistsCount}
            actions={groupStats.actionsCount}
            controls={groupStats.controlsCount}
            unreviewedActions={0}
            unreviewedControls={0}
            style={{ flex: 1 }}
          />
        ) : (
          <TextField
            clearable
            autoFocus={shouldAutoFocusSearchField}
            actionsMarginRight={0}
            onChange={handleSearch}
            onFocus={handleSearchFocus}
            onBlur={handleSearchBlur}
            value={searchValue}
            placeholder={`Search ${group.name}`}
            iconLeft={ICONS.MagnifyingGlassLight}
            onChangeDebounceMs={300}
            inputStyle={{ minHeight: 48 }}
            containerStyle={{
              minHeight: 48,
              flex: 1,
              backgroundColor: 'transparent',
            }}
          />
        )}
        <ViewWithSeparators
          horizontal
          separatorMargin={theme.paddingHorizontal / 2}
          separatorMarginOther={theme.paddingHorizontal / 2}
          separatorColor="#ddd"
        >
          {!showCountChips && <View />}
          <View
            style={{
              flexDirection: 'row',
              alignItems: 'center',
            }}
          >
            {!showSearch && showCountChips && (
              <IconButton
                icon={ICONS.MagnifyingGlassLight}
                onPress={() => {
                  autoFocusSearchFieldRef.current = true;
                  animateNext(() => setShowSearch(true));
                }}
              />
            )}
            <IconButton
              icon={ICONS.SlidersUpLight}
              onPress={() =>
                dispatch(
                  NAVIGATE_MODAL({
                    screen: ModalScreens.AseFieldInstancesOptions,
                    params: { groupId, listType: 'added' },
                  }),
                )
              }
              showBadge={hasEnabledFilterOptions}
            />
          </View>
          {(!!showQRButton || !!showPinButton) && (
            <View
              style={{
                flexDirection: 'row',
                alignItems: 'center',
              }}
            >
              {!!showQRButton && (
                <IconButton
                  icon={ICONS.QRCodeLight}
                  onPress={handleFindByQrPress}
                  accessibilityLabel="Find by QR"
                />
              )}
              {!!showPinButton && (
                <IconButton
                  icon={ICONS.LocationPinLight}
                  onPress={handleFindByPlanPress}
                  accessibilityLabel="Find on Plan"
                />
              )}
            </View>
          )}
          {group.allowPdfDownload && (
            <Menu
              iconButtonConfig={{
                icon: ICONS.ArrowDownLight,
              }}
              options={[
                {
                  text: 'Download Element PDFs',
                  subText: 'Get a ZIP of all PDFs',
                  onPress: handleDownloadElementZip,
                  icon: ICONS.ArrowDownLight,
                },
              ]}
            />
          )}
        </ViewWithSeparators>
      </View>
      {showCountChips && showSearch && (
        <TextField
          clearable
          autoFocus={shouldAutoFocusSearchField}
          indentActions
          onChange={handleSearch}
          onFocus={handleSearchFocus}
          onBlur={handleSearchBlur}
          value={searchValue}
          placeholder={`Search ${group.name}`}
          iconLeft={ICONS.MagnifyingGlassLight}
          onChangeDebounceMs={300}
          inputStyle={{ minHeight: 48 }}
          containerStyle={{
            minHeight: 48,
            backgroundColor: 'transparent',
            paddingLeft: theme.paddingHorizontal,
          }}
        />
      )}
    </ViewWithSeparators>
  );
};
